import { FC, useEffect, useRef } from 'react';
import { Col, Form } from 'antd';
import V2FormRadio from '@/common/components/Form/V2FormRadio/V2FormRadio';
import V2FormInputNumber from '@/common/components/Form/V2FormInputNumber/V2FormInputNumber';
import { useMethods } from '@lhb/hook';
import {
  BILLING_METHOD_OPTIONS,
  DEFAULT_BILLING_METHOD,
  isShowDiscount,
  isShowRent,
} from './config';

/**
 * @description 商务条件（businessTerms）复合定制组件
 * 由 3 个子字段组成，计费方式联动「月租金/扣点比例」显隐；
 * 提交/回显以整块对象存于 textValue，对应单个 identification。
 * 内部字段变动均经 updateCompValue 传出「整块对象」，避免 onValuesChange 以部分值覆盖 textValue。
 */
const BusinessTerms: FC<{
  propertyItem: any;
  form: any;
  disabled?: boolean;
  required?: boolean;
  /** 自定义组件变动时更新 textValue */
  updateCompValue?: any;
}> = ({
  propertyItem,
  form,
  disabled,
  required,
  updateCompValue,
}) => {
  const identification = propertyItem.identification;
  const value: any = Form.useWatch(identification, form);
  // 未设置计费方式时按默认（纯租金）处理
  const currentBusinessTermsType = value?.type || DEFAULT_BILLING_METHOD;
  const showRent = isShowRent(currentBusinessTermsType);
  const showDiscount = isShowDiscount(currentBusinessTermsType);
  // 避免默认值初始化重复执行
  const initedRef = useRef(false);

  const methods = useMethods({
    /**
     * @description 初始化默认计费方式
     * 编辑回显已有计费方式时不覆盖；无值时写入默认「纯租金」，保证提交时计费方式必有值。
     */
    initDefaultBusinessTermsType() {
      if (initedRef.current) return;
      const current = form.getFieldValue(identification) || {};
      initedRef.current = true;
      if (current?.type) return;
      const initVal = { ...current, type: DEFAULT_BILLING_METHOD };
      form.setFieldsValue({ [identification]: initVal });
      // setFieldsValue 不触发 onValuesChange，需手动同步 textValue
      updateCompValue?.(identification, initVal);
    },
    handleTypeChange(event) {
      methods.handleFieldChange('type', event.target.value);
    },
    handleRentChange(value) {
      methods.handleFieldChange('rent', value);
    },
    handleDiscountChange(value) {
      methods.handleFieldChange('discount', value);
    },
    /**
     * @description 内部字段变动，合并为整块对象后同步 textValue
     * @param fieldKey 内部字段名
     * @param fieldValue 该字段的新值
     */
    handleFieldChange(fieldKey: string, fieldValue: any) {
      const current = form.getFieldValue(identification) || {};
      updateCompValue?.(identification, { ...current, [fieldKey]: fieldValue });
    },
  });

  useEffect(() => {
    methods.initDefaultBusinessTermsType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Col span={12}>
        <V2FormRadio
          label='计费方式'
          name={[identification, 'type']}
          required={required ?? true}
          disabled={disabled}
          options={BILLING_METHOD_OPTIONS}
          onChange={methods.handleTypeChange}
        />
      </Col>
      {
        showRent ? <Col span={12}>
          <V2FormInputNumber
            label='月租金'
            name={[identification, 'rent']}
            required
            disabled={disabled}
            min={0}
            precision={2}
            config={{ addonAfter: '元/月' }}
            onChange={methods.handleRentChange}
          />
        </Col> : null
      }
      {
        showDiscount ? <Col span={12}>
          <V2FormInputNumber
            label='扣点比例（按销售额计提）'
            name={[identification, 'discount']}
            required
            disabled={disabled}
            min={0}
            precision={2}
            config={{ addonAfter: '%' }}
            onChange={methods.handleDiscountChange}
          />
        </Col> : null
      }
    </>
  );
};

export default BusinessTerms;
